<script setup>
import { computed, ref } from 'vue';
import PricingTable from 'vue-elements/product-info/PricingTable.vue';
import PricingTableHeader from 'vue-elements/product-info/PricingTableHeader.vue';
import PricingTableRow from 'vue-elements/product-info/PricingTableRow.vue';
import PriceSpecificationItem from '../product/PriceSpecificationItem.vue';

import Icon from 'vue-elements/icon/Icon.vue';
const props = defineProps({
    product: {
        type: Object,
        required: true,
    }
})

const isPriceCapped = computed(() => {
    let hasPriceCap = false;
    props.product.price_calculation.electricity_specification?.forEach((item) => {
        if (item.type === 'price_cap_electricity_single' || item.type === 'price_cap_electricity_normal' || item.type === 'price_cap_electricity_offpeak') {
            hasPriceCap = true;
        }
    });
    props.product.price_calculation.gas_specification?.forEach((item) => {
        if (item.type === 'price_cap_gas') {
            hasPriceCap = true;
        }
    });
    return hasPriceCap;
});

const isPriceCappedElectricity = computed(() => {
    let hasPriceCap = false;
    props.product.price_calculation.electricity_specification?.forEach((item) => {
        if (item.type === 'price_cap_electricity_single' || item.type === 'price_cap_electricity_normal' || item.type === 'price_cap_electricity_offpeak') {
            hasPriceCap = true;
        }
    });
    return hasPriceCap;
});

const isPriceCappedGas = computed(() => {
    let hasPriceCap = false;
    props.product.price_calculation.gas_specification?.forEach((item) => {
        if (item.type === 'price_cap_gas') {
            hasPriceCap = true;
        }
    });
    return hasPriceCap;
});

const expandedPricing = ref(false);

const solarRevenue = (props.product.price_calculation.electricity_unit_settled_total + props.product.price_calculation.electricity_unit_settled_total_off_peak + props.product.price_calculation.electricity_surplus_price) * -1;
</script>
<template>
    <div class="pricing-table-container">
        <pricing-table v-if="!expandedPricing">
            <pricing-table-header>
                <icon icon="x-fa-bolt" />
                <strong>Stroom</strong>
                <template v-if="product.tariff_type === 'dynamic' || product.tariff_type === 'dynamic_gas_fixed'">
                    (dynamisch)
                </template>
            </pricing-table-header>

            <template v-if="isPriceCappedElectricity">
                <pricing-table-row>
                    <template #description>
                        Prijsplafond (tot 2900 kWh)
                    </template>
                    <template #value>
                        <strong>{{ toCurrencyLongEur(0.4) }}</strong>
                    </template>
                </pricing-table-row>

                <pricing-table-row class="remove-border">
                    <template #description>
                        Vaste leveringskosten p/j
                    </template>
                    <template #value>
                        <strong>{{ toCurrencyEur(product.price_calculation.electricity_base) }}</strong>
                    </template>
                </pricing-table-row>

                <br>

                <pricing-table-row>
                    <template #description>
                        Boven 2900 kWh betaal je:
                    </template>
                </pricing-table-row>

                <pricing-table-row>
                    <template #description>
                        Normaaltarief per kWh
                    </template>
                    <template #value>
                        <strong>{{
                            toCurrencyLongEur(product.price_calculation.electricity_unit_price)
                            }}</strong>
                    </template>
                </pricing-table-row>

                <pricing-table-row v-if="product.price_calculation.electricity_double_meter" class="remove-border">
                    <template #description>
                        Daltarief per kWh
                    </template>
                    <template #value>
                        <strong>{{
                            toCurrencyLongEur(product.price_calculation.electricity_unit_price_off_peak)
                            }}</strong>
                    </template>
                </pricing-table-row>
            </template>

            <template v-else>
                <template
                    v-if="(product.tariff_type === 'dynamic' || product.tariff_type === 'dynamic_gas_fixed') && product.price_calculation.electricity_unit_purchase_costs">
                    <pricing-table-row>
                        <template #description>
                            Inkoopvergoeding per kWh
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyLongEur(product.price_calculation.electricity_unit_purchase_costs)
                                }}</strong>
                        </template>
                    </pricing-table-row>
                </template>

                <template v-else>
                    <pricing-table-row>
                        <template #description>
                            Normaaltarief per kWh
                        </template>
                        <template #value>
                            <strong>{{
                                toCurrencyLongEur(product.price_calculation.electricity_unit_price)
                                }}</strong>
                        </template>
                    </pricing-table-row>

                    <pricing-table-row v-if="product.price_calculation.electricity_double_meter">
                        <template #description>
                            Daltarief per kWh
                        </template>
                        <template #value>
                            <strong>{{
                                toCurrencyLongEur(product.price_calculation.electricity_unit_price_off_peak)
                                }}</strong>
                        </template>
                    </pricing-table-row>
                </template>

                <pricing-table-row class="remove-border">
                    <template #description>
                        Vaste leveringskosten p/j
                    </template>
                    <template #value>
                        <strong>{{ toCurrencyEur(product.price_calculation.electricity_base) }}</strong>
                    </template>
                </pricing-table-row>
            </template>

            <!-- Gas -->
            <template v-if="product.gas_included">
                <pricing-table-header>
                    <icon icon="x-fa-flame" />
                    <strong>Gas</strong>
                    <template v-if="product.tariff_type === 'dynamic'">
                        (dynamisch)
                    </template>
                    <template v-else-if="product.tariff_type === 'dynamic_gas_fixed'">
                        (vast)
                    </template>
                </pricing-table-header>

                <template v-if="isPriceCappedGas">
                    <pricing-table-row>
                        <template #description>
                            Prijsplafond (tot 1200 m³)
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyLongEur(1.45) }}</strong>
                        </template>
                    </pricing-table-row>

                    <pricing-table-row class="remove-border">
                        <template #description>
                            Vaste leveringskosten p/j
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyEur(product.price_calculation.gas_base) }}</strong>
                        </template>
                    </pricing-table-row>

                    <br>

                    <pricing-table-row>
                        <template #description>
                            Boven 1200 m³ betaal je:
                        </template>
                    </pricing-table-row>

                    <pricing-table-row class="remove-border">
                        <template #description>
                            Tarief per m<sup>3</sup>
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyLongEur(product.price_calculation.gas_unit_price) }}</strong>
                        </template>
                    </pricing-table-row>
                </template>
                <template v-else>
                    <pricing-table-row
                        v-if="product.tariff_type === 'dynamic' && product.price_calculation.gas_unit_purchase_costs">
                        <template #description>
                            Inkoopvergoeding per m<sup>3</sup>
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyLongEur(product.price_calculation.gas_unit_purchase_costs) }}</strong>
                        </template>
                    </pricing-table-row>

                    <pricing-table-row v-else>
                        <template #description>
                            Tarief per m<sup>3</sup>
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyLongEur(product.price_calculation.gas_unit_price) }}</strong>
                        </template>
                    </pricing-table-row>

                    <pricing-table-row class="remove-border">
                        <template #description>
                            Vaste leveringskosten p/j
                        </template>
                        <template #value>
                            <strong>{{ toCurrencyEur(product.price_calculation.gas_base) }}</strong>
                        </template>
                    </pricing-table-row>
                </template>
            </template>

        </pricing-table>
        <pricing-table v-if="!expandedPricing">
            <pricing-table-header v-if="product.price_calculation.electricity_generation">
                <icon icon="x-fa-solar-panel" />
                <strong>Zonnepanelen</strong>
            </pricing-table-header>

            <pricing-table-row v-if="product.price_calculation.electricity_generation" title="Terugleververgoeding per kWh">
                <template #description>
                    Terugleververgoeding<template v-if="product.tariff_type !== 'dynamic' && product.tariff_type !== 'dynamix_fixed_gas'"> per kWh</template>
                </template>
                <template #value>
                    <template v-if="product.tariff_type === 'dynamic' || product.tariff_type === 'dynamix_fixed_gas'">
                        dynamisch
                    </template>

                    <template v-else>
                        <strong>{{
                            toCurrencyMediumEur(product.price_calculation.electricity_surplus_tariff) }}</strong>
                    </template>
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="solarRevenue > 0 && product.tariff_type !== 'dynamic' && product.tariff_type !== 'dynamic_gas_fixed'" title="Opbrengsten per jaar uit salderen en terugleveren">
                <template #description>
                    Opbrengsten* p/j
                </template>
                <template #value>
                    <strong>{{ toCurrencyEur(solarRevenue) }}</strong>
                </template>
            </pricing-table-row>

            <pricing-table-row v-if="product.price_calculation.settling_costs_year">
                <template #description>
                    Terugleverkosten p/j
                </template>
                <template #value>
                    <strong>{{
                        toCurrencyEur(product.price_calculation.settling_costs_year)
                        }}</strong>
                </template>
            </pricing-table-row>

            <pricing-table-header>
                <icon icon="x-fa-receipt" />
                <strong>Totaal</strong>
            </pricing-table-header>

            <pricing-table-row v-if="product.total_discount > 0"
                class="discount-row remove-border">
                <template #description>
                    <span class="subtotal discount">Korting</span>
                </template>
                <template #value>
                    <strong class="subtotal discount">-&nbsp;{{ toCurrencyEur(product.total_discount) }}</strong>
                </template>
            </pricing-table-row>

            <pricing-table-row class="remove-border">
                <template #description>
                    <span class="subtotal" v-if="product.total_discount > 0">Per maand incl. korting</span>
                    <span class="subtotal" v-else>Per maand</span>
                </template>
                <template #value>
                    <strong>{{ toCurrencyEur(product.month_price) }}</strong>
                </template>
            </pricing-table-row>

        </pricing-table>

        <pricing-table class="expanded-pricing">
            <transition name="fade" mode="out-in">
                <div v-if="expandedPricing">
                    <pricing-table-header>
                        <icon icon="x-fa-bolt" />
                        Stroom
                    </pricing-table-header>

                    <pricing-table-row>
                        <template #description>
                            Vaste leveringskosten
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.price_calculation.electricity_base) }}
                        </template>
                    </pricing-table-row>

                    <price-specification-item
                        v-for="(specItem, index) in product.price_calculation.electricity_specification"
                        :key="'es-' + index" :item="specItem" unit="kWh" />

                    <pricing-table-row
                        v-if="product.price_calculation.electricity_surplus && product.price_calculation.electricity_surplus_bonus">
                        <template #description>
                            {{ product.electricity_feedin_bonus_name }}
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.price_calculation.electricity_surplus_bonus) }}
                        </template>
                    </pricing-table-row>

                    <pricing-table-row>
                        <template #description>
                            Netbeheerkosten
                            <template v-if="product.price_calculation.distributor_name">
                                ({{ product.price_calculation.distributor_name }})
                            </template>
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.price_calculation.electricity_network_costs) }}
                        </template>
                    </pricing-table-row>

                    <pricing-table-row>
                        <template #description>
                            Vermindering energiebelasting
                        </template>
                        <template #value>
                            - {{ toCurrencyEur(product.price_calculation.tax_discount) }}
                        </template>
                    </pricing-table-row>

                    <pricing-table-row>
                        <template #description>
                            <span class="subtotal">Totaal elektriciteit</span>
                        </template>
                        <template #value>
                            <span class="subtotal">{{
                                toCurrencyEur(product.price_calculation.electricity_total_with_tax_discount)
                                }}</span>
                        </template>
                    </pricing-table-row>

                    <template v-if="product.gas_included">
                        <pricing-table-header>
                            <icon icon="x-fa-flame" />
                            Gas
                        </pricing-table-header>

                        <pricing-table-row>
                            <template #description>
                                Vaste leveringskosten
                            </template>
                            <template #value>
                                {{ toCurrencyEur(product.price_calculation.gas_base) }}
                            </template>
                        </pricing-table-row>

                        <price-specification-item
                            v-for="(specItem, index) in product.price_calculation.gas_specification"
                            :key="'gs-' + index" :item="specItem" unit="m<sup>3</sup>" />

                        <pricing-table-row>
                            <template #description>
                                Netbeheerkosten
                                <template v-if="product.price_calculation.distributor_name">
                                    ({{ product.price_calculation.distributor_name }})
                                </template>
                            </template>
                            <template #value>
                                {{ toCurrencyEur(product.price_calculation.gas_network_costs) }}
                            </template>
                        </pricing-table-row>

                        <pricing-table-row>
                            <template #description>
                                <span class="subtotal">Totaal gas</span>
                            </template>
                            <template #value>
                                <span class="subtotal">{{
                                    toCurrencyEur(product.price_calculation.gas_total)
                                    }}</span>
                            </template>
                        </pricing-table-row>
                    </template>

                    <pricing-table-header>
                        <icon icon="x-fa-ballot" />
                        Totaal
                    </pricing-table-header>

                    <pricing-table-row>
                        <template #description>
                            Totaal per jaar
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.year_price_without_discount) }}
                        </template>
                    </pricing-table-row>
                    <template v-if="product.split_discounts && product.total_discount > 0">
                        <pricing-table-row
                            v-if="product.price_calculation.total_discount_electricity > 0 && product.gas_included"
                            class="discount-row">
                            <template #description>
                                <span class="subtotal">Korting op stroom</span>
                            </template>
                            <template #value>
                                <strong class="subtotal discount">-
                                    {{ toCurrencyEur(product.price_calculation.total_discount_electricity) }}</strong>
                            </template>
                        </pricing-table-row>
                        <pricing-table-row
                            v-if="product.price_calculation.total_discount_gas > 0 && product.gas_included"
                            class="discount-row">
                            <template #description>
                                <span class="subtotal">Korting op gas</span>
                            </template>
                            <template #value>
                                <strong class="subtotal discount">- {{
                                    toCurrencyEur(product.price_calculation.total_discount_gas)
                                    }}</strong>
                            </template>
                        </pricing-table-row>
                        <pricing-table-row v-if="product.total_discount > 0" class="discount-row ">
                            <template #description>
                                <span class="subtotal">Totale korting</span>
                            </template>
                            <template #value>
                                <span class="subtotal discount">- {{ toCurrencyEur(product.total_discount) }}</span>
                            </template>
                        </pricing-table-row>
                    </template>
                    <pricing-table-row v-if="!product.split_discounts && product.total_discount > 0"
                        class="discount-row ">
                        <template #description>
                            <span class="subtotal">Korting</span>
                        </template>
                        <template #value>
                            <span class="subtotal discount">- {{ toCurrencyEur(product.total_discount) }}</span>
                        </template>
                    </pricing-table-row>

                    <pricing-table-row v-if="product.total_discount > 0">
                        <template #description>
                            <span class="subtotal">Totaal per jaar inclusief korting</span>
                        </template>
                        <template #value>
                            <span class="subtotal">{{ toCurrencyEur(product.year_price) }}</span>
                        </template>
                    </pricing-table-row>

                    <pricing-table-header>
                        <icon icon="calendar" />
                        Gemiddelde prijs per maand
                    </pricing-table-header>

                    <pricing-table-row>
                        <template #description>
                            Prijs per maand zonder korting
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.month_price_without_discount) }}
                        </template>
                    </pricing-table-row>

                    <pricing-table-row v-if="product.total_discount > 0">
                        <template #description>
                            Prijs per maand inclusief korting
                        </template>
                        <template #value>
                            {{ toCurrencyEur(product.month_price) }}
                        </template>
                    </pricing-table-row>

                    <pricing-table-row>
                        <template #description>
                            <br>
                            Bovenstaande berekening is een indicatie op basis van je ingevulde verbruik. De
                            uiteindelijke kosten
                            zijn afhankelijk van je daadwerkelijke verbruik.<br><br>
                            <template v-if="isPriceCapped">
                                De 'Verbruikskosten (prijsplafond)' zijn zonder overheidsheffingen. Inclusief deze
                                overheidsheffingen gaat het om maximaal 40 cent per kWh voor stroom
                                <template v-if="product.gas_included">
                                    en maximaal 1,45 euro per m³ gas
                                </template>
                                .
                                <br><br>
                            </template>
                            Deze bedragen zijn inclusief {{ product.price_calculation.vat_percentage }}% btw,
                            overheidsheffingen
                            (energiebelasting en opslag duurzame energie) en eventuele toeslagen.<br><br>
                            Je netbeheerkosten zijn gebaseerd op een standaard elektriciteitsaansluiting van 3x25
                            ampère.
                            <template v-if="product.gas_included">
                                De netbeheerkosten voor je gasaansluiting zijn gebaseerd op een G4- of G6-aansluiting.
                            </template>
                        </template>
                    </pricing-table-row>
                </div>
            </transition>
        </pricing-table>

      <div class="tariff-notice" v-if="product.tariff_notice">{{ product.tariff_notice }}</div>

        <div class="tax-explanation">
            <template v-if="product.tariff_type === 'dynamic' || product.tariff_type === 'dynamic_gas_fixed'">
                Het geschatte jaarbedrag is gebaseerd op de verwachte beursprijzen voor komend jaar en is inclusief btw,
                overheidsheffingen en netbeheerkosten.
            </template>
            <template v-else>
                Deze bedragen zijn inclusief alle kosten: btw, overheidsheffingen en netbeheerkosten.
            </template>

            <template v-if="solarRevenue > 0 && product.tariff_type !== 'dynamic' && product.tariff_type !== 'dynamic_gas_fixed'">
                <br>
                <br>

                * De opbrengsten uit je zonnepanelen bestaan uit het voordeel uit salderen, het extra belastingvoordeel (minder energiebelasting) en de totale terugleververgoeding.
            </template>
        </div>

        <button class="more-info" :class="{ expanded: expandedPricing }" @click="expandedPricing = !expandedPricing">
            <span> {{ expandedPricing ? 'Inklappen' : 'Bekijk volledige prijs' }}</span>
            <Icon :icon="expandedPricing ? 'x-fa-chevron-circle-up' : 'x-fa-chevron-circle-down'" size="sm" />
        </button>
    </div>
</template>
